import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { AnimatePresence } from 'framer-motion';
import useOnClickOutside from 'hooks/click-outside';
import useBehavioral, { BEHAVIORALS } from 'hooks/useBehavioral';
import { MessageableType } from 'services/messages';
import { tiptapEditorFocusSelector } from 'store/reducers/global/global-reducer';
import { ThemeProvider } from 'styled-components';

import Comment, { IComment } from '../Comment';
import SubmitComment from '../SubmitComment';
import {
  CommentThreadContainer,
  CommentThreadContainerParentMessageContainer,
  CommentThreadInputContainer,
  CommentThreadReplyContainer,
  CommentThreadReplyLine,
  CommentThreadReplyLineContainer,
  CommentThreadWrapper,
} from './styled';

interface CommentThreadProps {
  message: IComment;
  messageable_type: MessageableType;
  messageable_uuid_or_slug: string;
  onDelete?: (comment: IComment) => void;
  onUpdate?: (comment: IComment) => void;
}

export default function CommentThread({
  message,
  messageable_type,
  messageable_uuid_or_slug,
  onDelete,
  onUpdate,
}: CommentThreadProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const targetMessageUuid = new URLSearchParams(location.search).get('message');
  const [replying, setReplying] = useState<boolean>(false);
  const [showChildMessages, setShowChildMessages] = useState<boolean>(false);
  const messageDeletedBehavioralFunction = useBehavioral(BEHAVIORALS.MESSAGE_DELETED);
  const tipTapEditorFocus = useSelector(tiptapEditorFocusSelector);

  const [messageState, setMessageState] = useState<IComment>(() => {
    const deletedMessages = message.child_messages.filter((m) => m.deleted_at);

    if (message.child_messages.length === deletedMessages.length) {
      return { ...message, child_messages: [] };
    }

    return message;
  });
  const [childMessages, setChildMessages] = useState<IComment[]>(() => {
    const deletedMessages = message.child_messages.filter((m) => m.deleted_at);

    if (message.child_messages.length === deletedMessages.length) {
      return [];
    }

    return message.child_messages;
  });

  useEffect(() => {
    if (childMessages.some((child) => child.uuid === targetMessageUuid && !child.deleted_at)) {
      setShowChildMessages(true);
    }
  }, [childMessages, targetMessageUuid]);

  useOnClickOutside(wrapperRef, () => {
    if (!tipTapEditorFocus) {
      setReplying(false);
    }
  });

  useEffect(() => {
    onUpdate?.(messageState);
  }, [messageState]);

  const handleReply = () => {
    setShowChildMessages(!replying);
    setReplying(!replying);
  };

  const handleFinishReply = (comment: IComment) => {
    const newChildMessages = [...childMessages, comment];
    setChildMessages(newChildMessages);
    setMessageState({
      ...messageState,
      child_messages: newChildMessages,
    });
    setReplying(false);
  };

  const sortedChildMessages = useMemo(() => {
    return childMessages.sort((a, b) => {
      return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    });
  }, [childMessages]);

  useEffect(() => {
    if (replying) {
      const inputContainer = document.querySelector('[data-id="comment-thread-input-container"]');
      const overallWrapper = document.querySelector('[data-id="resource-detail-overall-wrapper"]');
      if (inputContainer && overallWrapper) {
        overallWrapper.scrollTo({
          top: inputContainer.scrollTop,
          behavior: 'smooth',
        });
      }
    }
  }, [replying]);

  const handleChildMessageDelete = (comment: IComment) => {
    const newChildMessages = childMessages.filter((c) => c.uuid !== comment.uuid);
    setChildMessages(newChildMessages);
    setMessageState({
      ...messageState,
      child_messages: newChildMessages,
    });
  };

  const handleMessageDelete = () => {
    messageDeletedBehavioralFunction({
      related_object_uuid: messageable_uuid_or_slug,
      related_object_type: messageable_type,
      action_details: `Deleted message`,
    });
    onDelete?.(message);
  };

  const handleShowChildMessages = () => {
    setShowChildMessages(!showChildMessages);
  };

  const handleChildMessageAnimationComplete = () => {
    if (replying && showChildMessages) {
      const submitCommentWrapper = document.querySelector(
        `[data-id="submit-comment-wrapper-${message.uuid}"]`
      );
      if (submitCommentWrapper) {
        submitCommentWrapper.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }
    } else if (showChildMessages) {
      // The expanded thread must have its final height before focusing a linked reply.
      wrapperRef.current
        ?.querySelector<HTMLElement>('[data-highlighted="true"]')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  return (
    <ThemeProvider theme={{ hasCommentsOpen: showChildMessages }}>
      <CommentThreadWrapper ref={wrapperRef}>
        <CommentThreadContainer key={message.uuid}>
          <CommentThreadContainerParentMessageContainer replying={replying}>
            <Comment
              key={message.uuid}
              comment={messageState}
              onReply={handleReply}
              messageable_type={messageable_type}
              messageable_uuid_or_slug={messageable_uuid_or_slug}
              onDelete={handleMessageDelete}
              onShowChildMessages={handleShowChildMessages}
            />
          </CommentThreadContainerParentMessageContainer>
          <AnimatePresence>
            {childMessages.length > 0 && showChildMessages
              ? sortedChildMessages.map((childMessage, index) => (
                  <CommentThreadReplyContainer
                    lastOne={index === childMessages.length - 1}
                    firstOne={index === 0}
                    key={childMessage.uuid}
                    onAnimationComplete={handleChildMessageAnimationComplete}
                  >
                    <CommentThreadReplyLineContainer>
                      <CommentThreadReplyLine />
                      {index !== childMessages.length - 1 ? (
                        <CommentThreadReplyLine straight />
                      ) : null}
                    </CommentThreadReplyLineContainer>
                    <Comment
                      key={childMessage.uuid}
                      comment={childMessage}
                      onDelete={handleChildMessageDelete}
                      messageable_type={messageable_type}
                      messageable_uuid_or_slug={messageable_uuid_or_slug}
                      parent_uuid={message.uuid}
                    />
                  </CommentThreadReplyContainer>
                ))
              : null}
          </AnimatePresence>
          <AnimatePresence>
            {replying ? (
              <CommentThreadInputContainer
                hasReply={childMessages.length > 0}
                data-id="comment-thread-input-container"
              >
                <SubmitComment
                  messageable_type={messageable_type}
                  messageable_uuid_or_slug={messageable_uuid_or_slug}
                  parent_uuid={message.uuid}
                  onFinish={handleFinishReply}
                  autoFocus
                  placeholder="Reply..."
                />
              </CommentThreadInputContainer>
            ) : null}
          </AnimatePresence>
        </CommentThreadContainer>
      </CommentThreadWrapper>
    </ThemeProvider>
  );
}
